import { z } from 'zod';

/**
 * The on-account ledger. Append-only, signed integer amounts in the org's currency: order charges positive;
 * payments, discounts and credit notes negative; adjustments either sign. The balance is the sum. A correction is
 * a new entry whose `reverses_id` points at the one it cancels exactly. Amounts are net of tax.
 */
export const LEDGER_ENTRY_TYPES = [
  'order_charge',
  'payment',
  'discount',
  'credit_note',
  'adjustment',
] as const;
export const ledgerEntryTypeSchema = z.enum(LEDGER_ENTRY_TYPES);
export type LedgerEntryType = z.infer<typeof ledgerEntryTypeSchema>;

const minorUnits = z
  .number()
  .int()
  .refine((n) => n !== 0, 'zero');

/** A row of `public.ledger_entries`, as staff read it (`staff.ledger.view`). */
export const ledgerEntrySchema = z.object({
  id: z.uuid(),
  organization_id: z.uuid(),
  type: ledgerEntryTypeSchema,
  amount_cents: minorUnits,
  currency: z.enum(['USD', 'CAD']),
  order_id: z.uuid().nullable(),
  invoice_id: z.uuid().nullable(),
  reverses_id: z.uuid().nullable(),
  memo: z.string().nullable(),
  created_by: z.uuid().nullable(),
  created_at: z.iso.datetime({ offset: true }),
});
export type LedgerEntry = z.infer<typeof ledgerEntrySchema>;

/**
 * `customer_billing_summary(org)`: the balance and how much of it is invoiced and unpaid. Only for `billing.view`
 * while amounts are visible; otherwise the function returns no row at all.
 */
export const customerBillingSummarySchema = z.object({
  currency: z.enum(['USD', 'CAD']),
  balance_cents: z.number().int(),
  invoiced_unpaid_cents: z.number().int(),
  payment_mode: z.enum(['card', 'account']),
});
export type CustomerBillingSummary = z.infer<typeof customerBillingSummarySchema>;

/**
 * `customer_ledger(org)`: the history as a customer sees it. No staff memo and no author, by construction: the
 * function has no such columns.
 */
export const customerLedgerEntrySchema = z.object({
  id: z.uuid(),
  type: ledgerEntryTypeSchema,
  created_at: z.iso.datetime({ offset: true }),
  amount_cents: minorUnits,
  currency: z.enum(['USD', 'CAD']),
  order_id: z.uuid().nullable(),
  order_reference: z.string().nullable(),
  project_name: z.string().nullable(),
  invoice_ref: z.string().nullable(),
  invoice_status: z.enum(['issued', 'paid', 'void']).nullable(),
  is_reversal: z.boolean(),
});
export type CustomerLedgerEntry = z.infer<typeof customerLedgerEntrySchema>;

/** `staff_org_ledger(org)`: an entry as the staff pages show it, with its order, invoice, reversal and author. */
export const staffLedgerEntrySchema = ledgerEntrySchema
  .omit({ order_id: true, invoice_id: true })
  .extend({
    amount_cents: minorUnits,
    order_id: z.uuid().nullable(),
    order_reference: z.string().nullable(),
    project_name: z.string().nullable(),
    /** The entry's own invoice (discount, payment) or, for a charge, the live invoice it is on. */
    invoice_id: z.uuid().nullable(),
    invoice_ref: z.string().nullable(),
    invoice_status: z.enum(['issued', 'paid', 'void']).nullable(),
    reversed_by_id: z.uuid().nullable(),
    created_by_name: z.string().nullable(),
  });
export type StaffLedgerEntry = z.infer<typeof staffLedgerEntrySchema>;

/** A charge an invoice may still cover: an unreversed order charge on no live invoice. */
export function isInvoiceable(e: StaffLedgerEntry): boolean {
  return (
    e.type === 'order_charge' &&
    e.reverses_id === null &&
    e.reversed_by_id === null &&
    e.invoice_id === null
  );
}

/** Integer sum of entries, so balances add up in minor units exactly. */
export function sumCents(entries: readonly { amount_cents: number }[]): number {
  return entries.reduce((total, e) => total + e.amount_cents, 0);
}
